import { Select } from "./ui/select.tsx";
import { isAccountOnly } from "@zooi/domain";
import { AgentAssist } from "./agent-assist.tsx";
import { notify } from "./feedback.ts";
import { ProductMark } from "./ui/props.tsx";
import { ServiceLogo } from "./ui/service-logo.tsx";
import { ArrowSquareOutIcon, CheckCircleIcon, PencilSimpleIcon, PlusIcon, XIcon } from "@phosphor-icons/react";
import { useMutation } from "@tanstack/react-query";
import type * as C from "@zooi/contracts";
import { type FormEvent, type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { api } from "./api.ts";
import { Provenance, useRefresh } from "./components.tsx";
import { dateTime } from "./format.ts";
import { FactPlaybooks } from "./fact-playbooks.tsx";
import { BUSINESS_MODEL, BUSINESS_MODEL_ABOUT, CATEGORY_LABELS, categoryLabel, FACT_LABEL, KIT_LABELS, PROFILE_DESIGN as D, PRODUCT_MODEL_LABELS as M, PRODUCT_SOURCE_LABELS as L, READINESS_CHECK_LABELS, READINESS_LABELS, reasonText, SOURCE_REVIEW_LABELS, STAGE } from "./labels.ts";
import { GitHubTokenField, type ProductSourceForm, ProductSourceFields, RepositoryConsent, sourceRequest } from "./product-sources.tsx";
import { Button, Callout, EmptyState, ErrorText, Field, Section } from "./ui.tsx";
import { ListRow } from "./ui/list.tsx";
import { PropertyList, PropertyRow } from "./ui/props.tsx";
import { Chip } from "./ui/status.tsx";

// The blocks of a product's profile (docs/design-system.md §0.6, §0.3d): its
// core facts and categories as one boxed table, its properties beside them
// (stage and business model chosen in place), and on the sources tab where it
// is read from, the last read and what that read found. Forms open on demand.

export const EDITABLE = ["one_liner", "problem", "audience", "not_for"] as const;
const MAX_CATEGORIES = 4;

const host = (url: string) => url.replace(/^https?:\/\//, "").replace(/\/$/, "");
const coreFacts = (product: C.ProductDetail) => EDITABLE.map((key) => product.facts.find((fact) => fact.key === key));
/** A core fact counts as confirmed while it is confirmed and its sources have not changed since. */
const settled = (fact: C.Fact | undefined) => fact?.status === "confirmed" && !fact.stale_since;

/** Categories to pick, one to four, as the review card offers them. */
export function CategoryChecks({ value, onChange, autoFocus }: { value: string[]; onChange: (next: string[]) => void; autoFocus?: boolean }) {
  const id = useId();
  return (
    <ul className="z-check is-grid">
      {(Object.keys(CATEGORY_LABELS) as C.CategoryKey[]).map((k, index) => (
        <li key={k}>
          <input id={`${id}-${k}`} type="checkbox" autoFocus={autoFocus && index === 0} checked={value.includes(k)} onChange={(e) => onChange(e.target.checked ? [...value, k] : value.filter((v) => v !== k))} />
          <label htmlFor={`${id}-${k}`}>{CATEGORY_LABELS[k]}</label>
        </li>
      ))}
    </ul>
  );
}

/**
 * One fact in the table: its value (or what to write, while empty) and where
 * it came from; only the exceptions are marked. A sentence edits as text,
 * the categories as checks.
 */
function FactRow({ productId, fact, factKey }: { productId: string; fact: C.Fact | undefined; factKey: string }) {
  const refresh = useRefresh(productId);
  const list = factKey === "categories";
  const [editing, setEditing] = useState<string | string[] | null>(null);
  // Closing the editor returns focus to the button that opened it.
  const editButton = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);
  useEffect(() => {
    if (editing === null && wasEditing.current) editButton.current?.focus();
    wasEditing.current = editing !== null;
  }, [editing]);
  const save = useMutation({
    mutationFn: (value: string | string[]) => api.put(`/api/products/${productId}/facts/${factKey}`, { value }),
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
  });
  const confirm = useMutation({ mutationFn: () => api.post(`/api/products/${productId}/facts/confirm`, { keys: [fact!.key] }), onSuccess: refresh });
  const text = typeof fact?.value === "string" ? fact.value : "";
  const keys = list && Array.isArray(fact?.value) ? fact.value.map(String) : [];
  const has = list ? keys.length > 0 : Boolean(text);
  const name = FACT_LABEL[factKey] ?? factKey;
  const hint = D.hint[factKey as keyof typeof D.hint];
  const invalid = Array.isArray(editing) && (editing.length < 1 || editing.length > MAX_CATEGORIES);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (editing !== null && !invalid) save.mutate(editing);
  };
  const onKeys = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Escape") { e.preventDefault(); setEditing(null); }
    else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(e);
  };
  const stale = Boolean(fact?.stale_since);
  // The confirm button is the whole "not confirmed yet" signal: who proposed it is in the provenance line (§0.3d).
  const unconfirmed = Boolean(fact && has && fact.status !== "confirmed");
  return (
    <div className="z-fact" data-attention={stale ? "" : undefined}>
      <div className="z-fact-label">{name}</div>
      <div className="z-fact-body">
        {editing === null ? (
          <>
            {has
              ? list ? <p className="z-inline">{keys.map((k) => <Chip key={k}>{categoryLabel(k)}</Chip>)}</p> : <p className="z-fact-value">{text}</p>
              : <p className="z-fact-value z-muted">{hint}</p>}
            {fact && has && <Provenance provenance={fact.provenance} productId={productId} />}
          </>
        ) : (
          <form onSubmit={submit} onKeyDown={onKeys} className="z-flow">
            {typeof editing === "string"
              ? <textarea className="z-textarea" rows={3} aria-label={D.editFact(name)} placeholder={hint} value={editing} onChange={(e) => setEditing(e.target.value)} maxLength={240} autoFocus />
              : <div className="z-flow" role="group" aria-label={D.editFact(name)}><p className="z-small z-muted">{hint}</p><CategoryChecks value={editing} onChange={setEditing} autoFocus /></div>}
            <div className="z-inline">
              <Button tone="primary" size="sm" type="submit" busy={save.isPending} disabled={invalid}>{D.save}</Button>
              <Button size="sm" onClick={() => setEditing(null)}>{D.cancel}</Button>
              <span className="z-small z-muted">{D.editNote}</span>
            </div>
          </form>
        )}
        <ErrorText error={save.error ?? confirm.error} />
      </div>
      {editing === null && (
        <div className="z-fact-side">
          {stale && <Chip variant="soft" tone="amber" dot>{SOURCE_REVIEW_LABELS.needsReview}</Chip>}
          {unconfirmed && <Button size="sm" tone="soft" busy={confirm.isPending} onClick={() => confirm.mutate()}>{D.confirm}</Button>}
          <Button ref={editButton} size="sm" tone="ghost" icon={has ? <PencilSimpleIcon /> : <PlusIcon />} className={has ? "z-reveal" : undefined} aria-label={D.editFact(name)} onClick={() => setEditing(list ? keys : text)}>{has ? D.edit : D.add}</Button>
        </div>
      )}
    </div>
  );
}

/** The core facts a person confirms, one row each; only what needs them is marked. */
export function ProfileFacts({ product, stale, proposals, onReview }: { product: C.ProductDetail; stale: number; proposals: number; onReview: () => void }) {
  const facts = coreFacts(product);
  // An account has no source an agent could cite, so its facts are the person's own words (2026-11-03).
  const account = isAccountOnly({ url: product.url, repository: product.source.repository });
  return (
    <div className="z-flow">
      {stale + proposals > 0 && <Callout tone="warn">
        <div className="z-callout-row"><span>{D.reviewWaiting(stale, proposals)}</span><Button size="sm" onClick={onReview}>{D.startReview}</Button></div>
      </Callout>}
      {facts.every((fact) => !fact) && <EmptyState title={D.factsEmpty} icon={<CheckCircleIcon size={24} />} tone="amber">{account ? D.factsEmptyAccountNote : D.factsEmptyNote}</EmptyState>}
      <div className="z-boxed" aria-label={D.facts} role="group">
        {EDITABLE.map((key, index) => <FactRow key={key} productId={product.id} fact={facts[index]} factKey={key} />)}
        <FactRow productId={product.id} fact={product.facts.find((fact) => fact.key === "categories")} factKey="categories" />
      </div>
      {!account && <AgentAssist productId={product.id} task="profile" folded />}
      <FactPlaybooks productId={product.id} account={account} />
    </div>
  );
}

/**
 * The profile's properties column, about the product itself: how many core
 * facts are confirmed, the stage and business model (saved as soon as they
 * are chosen), the primary source and the last read. The website and
 * repository are under the title, access on the sources tab.
 */
export function ProfileProperties({ product }: { product: C.ProductDetail }) {
  const refresh = useRefresh(product.id);
  const save = useMutation({
    mutationFn: (body: { business_model?: string | null; stage?: string | null }) => api.patch<C.Product>(`/api/products/${product.id}`, body),
    onSuccess: refresh,
  });
  const facts = coreFacts(product);
  const source = product.source;
  const stage = STAGE[product.stage.value].label;
  return (
    <aside className="z-detail-summary is-stacked" aria-label={KIT_LABELS.properties}>
      <h2>{KIT_LABELS.properties}</h2>
      <PropertyList>
        <PropertyRow label={D.coreFacts}>{D.confirmedOf(facts.filter(settled).length, facts.length)}</PropertyRow>
        <PropertyRow label={D.stageShort}>
          <Select variant="property" label={D.stage} title={M.stageHelp} disabled={save.isPending} value={product.stage.by === "person" ? product.stage.value : ""} onValueChange={(nextValue) => save.mutate({ stage: nextValue || null })} options={[{ value: "", label: product.stage.by === "sources" ? M.fromSourcesValue(stage) : M.fromSources }, ...Object.entries(STAGE).map(([value, s]) => ({ value: value, label: s.label }))]} />
        </PropertyRow>
        <PropertyRow label={D.businessModel}>
          <Select variant="property" label={D.businessModel} title={M.businessModelHelp} disabled={save.isPending} value={product.business_model ?? ""} onValueChange={(nextValue) => save.mutate({ business_model: nextValue || null })} options={[{ value: "", label: M.notSet }, ...Object.entries(BUSINESS_MODEL).map(([value, label]) => ({ value, label, description: BUSINESS_MODEL_ABOUT[value as keyof typeof BUSINESS_MODEL_ABOUT] }))]} />
        </PropertyRow>
        <PropertyRow label={D.primarySource}>{source.source_of_truth === "repository" ? L.repositorySource : L.websiteSource}</PropertyRow>
        <PropertyRow label={D.lastRead}>{product.first_read && dateTime(product.first_read.read_at, product.settings.timezone)}</PropertyRow>
      </PropertyList>
      <ErrorText error={save.error} />
    </aside>
  );
}

/** The website, repository and X account the first read observed, by key. */
const observed = (product: C.ProductDetail, key: "website" | "repository" | "x_account") => {
  const value = product.facts.find((fact) => fact.key === key)?.value;
  return value && typeof value === "object" ? value as Record<string, unknown> : null;
};

function OpenLink({ href, label }: { href: string; label: string }) {
  return <a className="z-iconbtn" href={href} target="_blank" rel="noreferrer noopener" aria-label={label} title={label}><ArrowSquareOutIcon size={14} /></a>;
}

/**
 * Where zooi reads the product from, one row each, and on demand the form to
 * change them and read-only repository access; changing a repository needs
 * consent first.
 */
export function ProductSources({ product }: { product: C.ProductDetail }) {
  const refresh = useRefresh(product.id);
  const saved = product.source;
  const [editing, setEditing] = useState(product.status === "read_failed");
  const [source, setSource] = useState<ProductSourceForm>({
    repository: saved.repository ?? "",
    repositoryRef: saved.repository_ref ?? "",
    sourceOfTruth: saved.source_of_truth,
    websiteStatus: saved.website_status,
  });
  const [websiteUrl, setWebsiteUrl] = useState(product.url);
  const [consent, setConsent] = useState(false);
  const [accessConsent, setAccessConsent] = useState(false);
  const [token, setToken] = useState("");
  useEffect(() => {
    setSource({ repository: saved.repository ?? "", repositoryRef: saved.repository_ref ?? "", sourceOfTruth: saved.source_of_truth, websiteStatus: saved.website_status });
    setWebsiteUrl(product.url);
    setConsent(false);
    setAccessConsent(false);
    setToken("");
  }, [product.id, product.url, saved.repository, saved.repository_ref, saved.source_of_truth, saved.website_status]);
  const hasRepository = source.repository.trim().length > 0;
  const sourceChanged = source.repository.trim() !== (saved.repository ?? "")
    || source.repositoryRef.trim() !== (saved.repository_ref ?? "")
    || source.sourceOfTruth !== saved.source_of_truth
    || source.websiteStatus !== saved.website_status;
  const changed = sourceChanged || websiteUrl.trim() !== product.url;
  const save = useMutation({
    mutationFn: () => api.patch<C.Product>(`/api/products/${product.id}`, { ...sourceRequest(source, consent), url: websiteUrl.trim() }),
    onSuccess: async () => {
      setConsent(false);
      notify.success(L.sourceSaved);
      setEditing(false);
      await refresh();
    },
  });
  const authorize = useMutation({
    mutationFn: () => api.post(`/api/products/${product.id}/repository-access`, { token: token.trim(), repository_consent: true }),
    onSuccess: async () => {
      setToken("");
      setAccessConsent(false);
      notify.success(L.accessSaved);
      await refresh();
    },
  });
  const revoke = useMutation({
    mutationFn: () => api.del(`/api/products/${product.id}/repository-access`),
    onSuccess: async () => {
      setToken("");
      setAccessConsent(false);
      notify.success(L.accessSaved);
      await refresh();
    },
  });
  const busy = save.isPending || authorize.isPending || revoke.isPending;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!changed || (sourceChanged && hasRepository && !consent)) return;
    save.mutate();
  };
  const primary = (kind: "repository" | "website") => saved.source_of_truth === kind ? D.primarySource : D.observedByRead;
  const repository = observed(product, "repository");
  const website = observed(product, "website");
  const x = observed(product, "x_account");
  const stars = typeof repository?.stars === "number" ? L.stars(repository.stars) : null;
  return (
    <Section id="product-sources" title={L.section} actions={
      <Button size="sm" tone="ghost" icon={editing ? <XIcon /> : <PencilSimpleIcon />} aria-expanded={editing} onClick={() => setEditing(!editing)}>{editing ? D.cancel : D.change}</Button>
    }>
      <div className="z-boxed">
        {saved.repository && <ListRow icon={<ServiceLogo service="github" />} title={saved.repository}
          sub={[primary("repository"), saved.repository_ref ?? L.repositoryRefPlaceholder, saved.github_error ? D.accessRefused : saved.github_authorized ? D.accessAuthorized : D.accessPublic, stars].filter(Boolean).join(" · ")}
          end={<OpenLink href={`https://github.com/${saved.repository}`} label={D.openSource(saved.repository)} />} />}
        <ListRow icon={<ProductMark productId={product.id} url={product.url} name={product.name} />} title={host(product.url)}
          sub={[saved.website_status === "previous_version" ? L.websitePrevious : primary("website"), typeof website?.title === "string" ? website.title : null].filter(Boolean).join(" · ")}
          end={<OpenLink href={product.url} label={D.openSource(host(product.url))} />} />
        {typeof x?.handle === "string" && <ListRow icon={<ServiceLogo service="x" />} title={x.handle} sub={D.observedByRead} />}
      </div>
      {editing && <div className="z-flow z-edit-block">
        <form onSubmit={submit} className="z-form z-flow">
          <Field label={L.website} help={L.websiteHelp}>
            <input className="z-input" type="url" aria-label={L.website} value={websiteUrl} maxLength={500} required disabled={busy} onChange={(event) => setWebsiteUrl(event.target.value)} />
          </Field>
          <ProductSourceFields value={source} disabled={busy} onChange={(value) => {
            setSource(value);
            setConsent(false);
            setAccessConsent(false);
            setToken("");
          }} />
          {sourceChanged && hasRepository && <RepositoryConsent checked={consent} onChange={setConsent} disabled={busy} />}
          {changed && <p className="z-small z-muted">{L.changeNote}</p>}
          <div>
            <Button type="submit" tone="primary" busy={save.isPending} disabled={busy || !changed || (sourceChanged && hasRepository && !consent)}>{L.save}</Button>
          </div>
          <ErrorText error={save.error} />
        </form>
        {saved.repository && !changed && (
          <form className="z-form z-flow" onSubmit={(e) => {
            e.preventDefault();
            if (!token.trim() || !accessConsent) return;
            authorize.mutate();
          }}>
            <GitHubTokenField value={token} onChange={setToken} disabled={busy} required />
            <RepositoryConsent checked={accessConsent} onChange={setAccessConsent} disabled={busy} />
            <p className="z-small z-muted">{L.readOnly}</p>
            <div className="z-inline">
              <Button type="submit" busy={authorize.isPending} disabled={busy || !token.trim() || !accessConsent}>{L.authorize}</Button>
              {saved.github_authorized && <Button tone="danger" busy={revoke.isPending} disabled={busy} onClick={() => revoke.mutate()}>{L.revoke}</Button>}
            </div>
            <ErrorText error={authorize.error ?? revoke.error} />
          </form>
        )}
      </div>}
    </Section>
  );
}

/** The last read: at which ref and commit, which files, and what it noted; quiet text, not a warning. When is in the properties column. */
export function LastRead({ product }: { product: C.ProductDetail }) {
  const read = product.first_read;
  if (!read) return null;
  const value = observed(product, "repository");
  const fullName = typeof value?.full_name === "string" ? value.full_name : null;
  const sources = read.sources && (!fullName || read.sources.repository?.toLowerCase() === fullName.toLowerCase()) ? read.sources : undefined;
  const repository = sources?.repository ?? fullName;
  const ref = sources?.ref ?? (typeof value?.ref === "string" ? value.ref : null);
  const commit = sources?.commit ?? (typeof value?.commit === "string" ? value.commit : null);
  const files = sources?.files ?? (Array.isArray(value?.files) ? value.files.filter((file): file is string => typeof file === "string") : []);
  const partial = sources?.partial ?? value?.partial === true;
  const located = Boolean(ref || commit || files.length);
  if (!located && !read.notes.length) return null;
  return (
    <Section title={D.lastRead}>
      {located && <PropertyList>
        {ref && <PropertyRow label={L.observedRef}><code>{ref}</code></PropertyRow>}
        {commit && <PropertyRow label={L.observedCommit}>{repository
          ? <a className="z-link" href={`https://github.com/${repository}/commit/${encodeURIComponent(commit)}`} target="_blank" rel="noreferrer noopener" title={commit}><code>{commit.slice(0, 12)}</code></a>
          : <code>{commit.slice(0, 12)}</code>}</PropertyRow>}
        {files.length > 0 && <PropertyRow label={D.files}>
          <details className="z-disclosure">
            <summary>{L.observedFiles(files.length)}{partial ? ` · ${L.partialSample}` : ""}</summary>
            <ul className="z-points z-small">{files.map((file) => <li key={file}><code>{file}</code></li>)}</ul>
          </details>
        </PropertyRow>}
      </PropertyList>}
      {read.notes.length > 0 && <ul className="z-points z-small z-muted">{read.notes.map((note, i) => <li key={i}>{reasonText(note)}</li>)}</ul>}
    </Section>
  );
}

/** What the read found ready, missing or unknown, one line each; what the checks cannot tell is said once below. */
export function Readiness({ product }: { product: C.ProductDetail }) {
  const read = product.first_read;
  if (!read) return null;
  return (
    <Section title={READINESS_LABELS.title}>
      <ul className="z-check">
        {product.readiness_checks.map(({ key, value }) => (
          <li key={key}>
            <Chip variant="soft" dot tone={value === null ? "gray" : value ? "green" : "orange"}>{value === null ? "–" : value ? READINESS_LABELS.found : READINESS_LABELS.notFound}</Chip>
            <span>{READINESS_CHECK_LABELS[key]}</span>
          </li>
        ))}
        {(product.business_model === "self_serve_software" || product.business_model === "developer_tool" || product.business_model === "open_source") && read.readiness?.releases_read && (
          <li>
            <Chip variant="soft" dot tone={read.readiness.days_since_release != null && read.readiness.days_since_release <= 30 ? "green" : "orange"}>
              {read.readiness.days_since_release == null ? READINESS_LABELS.noRelease : READINESS_LABELS.releaseAge(read.readiness.days_since_release)}
            </Chip>
            <span>{READINESS_LABELS.releaseNote}</span>
          </li>
        )}
      </ul>
      <p className="z-small z-muted">{READINESS_LABELS.note}</p>
    </Section>
  );
}
